// ABOUTME: Provider for toast notifications — mounts the Toaster and supplies handleNotification
// ABOUTME: The context and hook live in useNotificationContext.ts

import type { ReactNode } from "react"
import React, { useCallback, useMemo } from "react"
import { Toaster, toast } from "sonner"
import { NotificationContext, type NotificationContextProps, type ToastType } from "./useNotificationContext"
import { SlowSaveNotice } from "./SlowSaveNotice"

/** The state as an 18px filled dot with a white tick, the way the mock draws it. */
const StatusDot = ({ className }: { className: string }) => (
  <span className={`flex size-4.5 flex-none items-center justify-center rounded-full text-white ${className}`}>
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  </span>
)

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const handleNotification = useCallback((type: ToastType, message: string, _loading: boolean, timeout: number) => {
    if (type === "success") {
      toast.success(message, { duration: timeout })
    } else {
      toast.error(message, { duration: timeout })
    }
  }, [])
  const value = useMemo<NotificationContextProps>(() => ({ handleNotification }), [handleNotification])
  return (
    <NotificationContext.Provider value={value}>
      {/* The toast of the design system: a pill under the page header, white on
          a hairline, the state as a filled dot and the close button inline
          after the text (docs/design/Рационы и библиотека.dc.html). Sonner's
          own close button floats in the corner, hence the reset below. */}
      <Toaster
        position="top-center"
        closeButton
        offset={72}
        mobileOffset={16}
        icons={{
          success: <StatusDot className="bg-green-600" />,
          error: <StatusDot className="bg-destructive" />,
        }}
        toastOptions={{
          classNames: {
            toast:
              "group toast !w-max !max-w-[calc(100vw-32px)] !gap-2.5 !rounded-[10px] !border !border-border !bg-white !py-2.5 !pr-2.5 !pl-3 !shadow-toastShadow",
            title: "!text-sm !font-medium !text-gray-900",
            description: "!text-[13px] !text-muted-foreground",
            closeButton:
              "!static !order-last !ml-auto !size-6 !translate-x-0 !translate-y-0 !rounded-md !border-none !bg-transparent !text-gray-400 hover:!bg-gray-100 hover:!text-gray-600",
            actionButton: "group-[.toast]:bg-primary group-[.toast]:text-white",
            cancelButton: "group-[.toast]:bg-gray-100 group-[.toast]:text-gray-600",
          },
        }}
      />
      <SlowSaveNotice />
      {children}
    </NotificationContext.Provider>
  )
}
